import { useQuery } from '@apollo/client';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { getClinicsForLabTemplate } from 'apollo/templates/organization';
import { Organization } from 'models/organization';
import React, { FC, Fragment } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';

type ClinicFilterProps = {
    onClinicFilterChange: (_: React.SyntheticEvent, clinic: Organization | null) => void;
    selectedClinic?: Organization | null;
    onlyCollective?: boolean;
    includeCollectiveDentist?: boolean;
    showParents?: boolean;
    invoiceEnabled?: boolean;
};

const ClinicFilter: FC<ClinicFilterProps> = ({
    onClinicFilterChange,
    selectedClinic,
    showParents = false,
    onlyCollective = false,
    includeCollectiveDentist = true,
    invoiceEnabled = true,
}: ClinicFilterProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { data: { clinicsForLab } = {}, loading } = useQuery(getClinicsForLabTemplate, {
        variables: {
            filters: {
                invoiceEnabled: invoiceEnabled,
                isCollective: onlyCollective,
                showParents: showParents,
                includeCollectiveDentist,
            },
        },
        onError(error) {
            showBoundary(error);
        },
    });
    return (
        <Fragment>
            {!loading && (
                <FormControl fullWidth>
                    <Autocomplete
                        id="clinic-select"
                        options={clinicsForLab || []}
                        getOptionLabel={(clinic: Organization) => `${clinic.name || ''}`}
                        renderOption={(props, option: Organization) => (
                            <li {...{ ...props, key: option?._id }}>
                                {option.name} &nbsp;
                                {!option.parentClinicId && !option.local && option.isMain ? (
                                    <i> ({translate('common:MAIN')})</i>
                                ) : undefined}
                            </li>
                        )}
                        isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                        value={(selectedClinic || { name: '' }) as Organization}
                        onChange={onClinicFilterChange}
                        renderInput={(params) => <TextField {...params} label={translate('common:CLINIC')} />}
                    />
                </FormControl>
            )}
        </Fragment>
    );
};

export default ClinicFilter;
